import * as React from 'react';
import styled from 'styled-components';
import InputLabel2 from '../inputLabel/InputLabel2';

// ############################ Imported from Old UI ###########################

// tslint:disable-next-line:variable-name
const SInputOuterWrap = styled.fieldset`
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 800px) {
    flex-basis: 0;
  }
`;

// tslint:disable-next-line:variable-name
const SInputInnerWrap = styled.div<{ error: boolean; disabled: boolean; background: InputBackground }>(
  ({ theme, error, disabled, background }) => {
    const disabledStyle = disabled ? { opacity: 0.5, WebkitTextFillColor: theme.colors.black } : {};

    return {
      backgroundColor: background,
      border: error ? `1px solid ${theme.colors.error}!important` : `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadiusCommon,
      position: 'relative',
      ...disabledStyle,
    };
  }
);

// tslint:disable-next-line:variable-name
const SInput = styled.input<{ blue: boolean; centeredText: boolean }>(({ theme, blue, centeredText }) => ({
  fontWeight: theme.font.weight.light,
  fontSize: theme.font.size.smallMedium2,
  lineHeight: '22px',
  boxSizing: 'border-box',
  color: theme.colors.grayDark,
  backgroundColor: 'transparent',
  borderWidth: 0,
  appearance: 'none',
  outline: 'none',
  padding: `${theme.spacing.medium}px ${theme.spacing.large}px`,
  width: '100%',
  textAlign: centeredText ? 'center' : 'initial',
  '&:invalid': {
    boxShadow: 'none',
  },
  '::placeholder': {
    fontWeight: theme.font.weight.medium,
    color: blue ? theme.colors.light : theme.colors.placeholder,
    fontSize: theme.font.size.smallMedium,
  },
  '&:focus': {
    '::placeholder': {
      color: 'transparent',
    },
  },
}));

// tslint:disable-next-line:variable-name
const SErrorMessage = styled.span(({ theme }) => ({
  fontWeight: theme.font.weight.light,
  position: 'absolute',
  fontSize: theme.font.size.small,
  left: 7,
  bottom: -20,
  color: theme.colors.error,
}));

const MUITextFIeld = styled.div(
  ({ theme }) => `
  position: relative;
	margin-bottom: 15px;
	& label::before {
		content: attr(title);
		position: absolute;
		top: 0;
		left: 30px;
		line-height: 40px;
		font-size: 14px;
		color: #777;
		transition: 300ms all;
	}
  & input {
    width: 100%;
    line-height: 40px;
    padding: 0 15px;
    box-sizing: border-box;
    font-size: 14px;
    color: #222;
    border: 1px solid #ccc;
    border-radius: 3px;
		&:focus {
			outline: 0;
			border-color: ${theme.colors.primaryBright};
		}
		&:valid + label::before, &:focus + label::before, &:not([value=""]) ~ label::before {
			line-height: 20px;
      font-size: 12px;
      top: -10px;
      background: #fff;
      padding: 0 6px;
      left: 9px;
		}
		&:valid + label::before {
			content: attr(data-title);
    }
		&:focus + label::before {
			color: blue;
		}
    &#error:focus {
			outline: 0;
			border-color: red;
		}
    &#error:focus + label::before {
			color: red;
		}
	}`
);

export enum InputType {
  Text = 'text',
  Number = 'number',
  Email = 'email',
  Password = 'password',
}

export enum InputBackground {
  White = 'white',
  Gray = '#f6f6f6',
}

export interface IInputProps {
  type?: InputType;
  name: string;
  value: string;
  disabled?: boolean;
  readOnly?: boolean;
  blue?: boolean;
  pattern?: string;
  maxLength?: number;
  autoComplete?: string;
  enableAutoComplete?: boolean;
  placeholder?: string;
  animatedLabel?: string;
  className?: string;
  centeredText?: boolean;
  dataAt?: string;
  label?: string | React.ReactChild;
  error?: string;
  background?: InputBackground;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  innerRef?: () => React.RefObject<HTMLInputElement>;
  isAnimated?: boolean;
}

export default class Input2 extends React.Component<IInputProps> {
  public static defaultProps = {
    type: InputType.Text,
    disabled: false,
    readOnly: false,
    enableAutoComplete: false,
    blue: false,
    value: '',
    centeredText: false,
  };

  private handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (this.props.pattern && !new RegExp(this.props.pattern).test(value)) {
      return;
    }
    if (this.props.onChange !== undefined) {
      this.props.onChange(event);
    }
  };

  public render() {
    let inputMode: React.HtmlHTMLAttributes<HTMLInputElement>['inputMode'];
    let inputPattern;

    if (this.props.type === InputType.Number) {
      inputMode = 'numeric';
      inputPattern = '[0-9]*';
    }

    const inputOptions = {
      inputMode,
      pattern: inputPattern,
      id: this.props.name,
      name: this.props.name,
      value: this.props.value,
      type: this.props.type === InputType.Password ? InputType.Password : InputType.Text,
      checked: !!this.props.value,
      readOnly: this.props.disabled,
      blue: this.props.blue || false,
      animatedLabel: this.props.animatedLabel,
      placeholder: this.props.placeholder,
      autoComplete: this.props.autoComplete ? this.props.autoComplete : 'off',
      autoCapitalize: 'none',
      autoCorrect: 'off',
      maxLength: this.props.maxLength,
      'data-at': this.props.dataAt,
      'data-neuro-label': this.props.name || this.props.dataAt,
      onChange: this.handleOnChange,
      onClick: this.props.onClick,
      onKeyUp: this.props.onKeyUp,
      centeredText: Boolean(this.props.centeredText),
    };

    const labelText = this.props.label && typeof this.props.label === 'string' && this.props.label.toString();
    const titleText = this.props.animatedLabel || this.props.placeholder || labelText;

    return (
      <SInputOuterWrap className={this.props.className}>
        <MUITextFIeld>
          {this.props.label && !this.props.isAnimated && (
            <InputLabel2 inputId={this.props.name} inputValue={this.props.value} dataAt={`${this.props.dataAt}-label`}>
              {this.props.label}
            </InputLabel2>
          )}
          <SInputInnerWrap
            className="inputInnerWrap"
            background={this.props.background || InputBackground.Gray}
            error={Boolean(this.props.error)}
            disabled={this.props.disabled || false}
          >
            <SInput required tabIndex={0} {...inputOptions} ref={this.props.innerRef} />
            {this.props.isAnimated && (
              <label htmlFor={this.props.name} title={titleText} data-title={titleText}></label>
            )}
          </SInputInnerWrap>
        </MUITextFIeld>
        {this.props.error && (
          <SErrorMessage tabIndex={0} data-at={`${this.props.name}Error`}>
            {this.props.error}
          </SErrorMessage>
        )}
      </SInputOuterWrap>
    );
  }
}
